import React, { useState, useEffect, useRef, useContext } from "react";
import {ReviewsContext} from "./Context";
import Modal from "react-bootstrap/Modal";
import ReviewCard from "./ReviewCard";
import Container from "react-bootstrap/Container";
import Card from 'react-bootstrap/Card';
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import CommentCard from "../comments/CommentCard";
import {JSON_HEADERS} from "../../constants/constants";
import PostingActions from "../postings/PostingActions";
import Collapse from "react-bootstrap/Collapse";
import CommentForm from "../comments/CommentForm";
import ProductCard from "../products/ProductCard";
import ListGroup from "react-bootstrap/ListGroup";
import SectionHeaderTag from "../misc/SectionHeaderTag";
import {isMobile} from "react-device-detect";
import {SENTIMENT_SCORE_TOOLTIP} from "../../constants/reviewsConstants";
import TooltipTrigger from "../bootstrap/TooltipTrigger";
import {valueExists} from "../../utils/utilsHelpers";
import AuthorSideBar from "../users/AuthorSideBar";

function ReviewModal(props) {
    let { review,
        product,
        relatedProducts,
        reviewSections,
        show,
        report,
        token,
        showComments=true,
        showNewCommentForm=true,
        showReviewActions=true,
        reviewCount
    } = props;
    // also in props: onCancel

    const {
        leadAttributes,
        selectedAudio,
        currentUser,
        tooltips,
        onToggleAudio,
        onToggleLeadGen
    } = useContext(ReviewsContext);

    const [comments, setComments] = useState(review.comments);
    const [showMoreComments, setShowMoreComments] = useState(review.comment_count > 2);
    const [commentCount, setCommentCount] = useState(review.comment_count);
    const [showCommentForm, setShowCommentForm] = useState(!review.comment_count);
    const modalBodyRef = useRef(null);

    const genericLeadData = {
        leadCode: 'PEER_RESEARCH_USERS',
        gtmEventRegion: 'review-modal',
        gtmEventElement: 'register',
        modalOverlay: true,
        cta: 'Register',
        confirmationParams: {
            header: 'Thank you' },
        hiddenTags: {
            ...review.lead_gen_attributes, todo: 'generic_lead', itemid: review.id, 'lead[product_id]': product.id, 'lead[lead_type]': 'ProductReviewLead', 'lead[lead_type_data]': review.id, 'lead[lead_code]': 'PEER_RESEARCH_USERS' }
    }

    useEffect(() => {
       // when review changes (from clicking next/prev, update state variables)
        setComments(review.comments);
        setShowMoreComments(review.comment_count > 2);
        setCommentCount(review.comment_count);
        setShowCommentForm(!review.comment_count);
    }, [review]);

    useEffect(() => {
        if(!props.forwardClick) {
            if(props.isShareLink) {
                history.replaceState({}, null, location.pathname);
            }
            history.pushState({}, null, location.href);
        }
    }, []);

    useEffect(() => {
        const onBackClicked = () => {
            props.onCancel(false); // we don't want to manually replace the url because the browser btn does it automatically
        };

        const onForwardClicked = () => {
            const reviewId = +new URLSearchParams(location.search).get('review_id');
            if(!!reviewId) {
                // the browser's forward link was clicked
                props.forwardModalToggle(reviewId)
            }
        }

        if(show) {
            props.onReplaceReviewURL(review.id);
            window.addEventListener('popstate', onBackClicked);
            window.addEventListener('popstate', onForwardClicked);
        }

        return () => {
            window.removeEventListener('popstate', onBackClicked);
        };
    }, [show]);

    const onToggleCommentForm = (show) => {
        setShowCommentForm(show);
    }

    const onNewComment = (comment) => {
        let newComments = [comment, ...comments];
        setComments(newComments);
        setShowCommentForm(false);
        let newCommentCount = commentCount + 1;
        setCommentCount(newCommentCount);
        props.onCommentsChangeHandler(review.id, newComments, newCommentCount);
    }

    const onDeleteComment = (commentId) => {
        let currComments = [...comments];
        let newComments = currComments.filter((c) => c.id != commentId);
        setComments(newComments);
        let newCommentCount = commentCount - 1;
        setCommentCount(newCommentCount);
        props.onCommentsChangeHandler(review.id, newComments, newCommentCount);
    }

    const onRepliesChangeHandler = (comment) => {
        let newComments = comments.map(c => {
            return (c.id === comment.id) ? comment : c;
        });
        props.onCommentsChangeHandler(review.id, newComments, commentCount);
    }

    const onListComments = async() => {
        await fetch(`/comments/list?posting_id=${review.id}`, {
            method: 'GET',
            headers: JSON_HEADERS
        }).then(function(response) {
            return response.json();
        }).then(function(jsonResponse) {
            setComments(jsonResponse.comments);
            setShowMoreComments(false);
        });
    }

    const renderReviewActions = () => {
        if(showReviewActions) {
            return (
                <PostingActions
                    posting={review}
                    type={'product_review'}
                    currentUser={currentUser}
                    hoverBg={'light'}
                    onToggleCommentForm={onToggleCommentForm}
                    showCommentLink={!!comments.length}
                    commentIsOpen={showCommentForm}
                    token={token}
                    toggleRegistration={onToggleLeadGen}
                    leadData={genericLeadData}
                />
            )
        }
    }

    const renderCommentForm = () => {
        if(showNewCommentForm && showCommentForm) {
            return (
                <Collapse in={showCommentForm}>
                    <div id={'toggleAnswerForm'} className={'my-2'}>
                        <CommentForm
                            currentUser={currentUser}
                            token={token}
                            parentId={review.id}
                            parentType={'review'}
                            onNewComment={onNewComment}
                            redirectUrl={review.actions.url}
                            toggleRegistration={onToggleLeadGen}
                            leadData={genericLeadData}
                        />
                    </div>
                </Collapse>
            )
        }
    }

    const renderComments = () => {
        if(showComments) {
            if(comments.length) {
                return (
                    <div className={'posting-comments'}>
                        <SectionHeaderTag text={`${commentCount} Comments`} classes={'pb-2'} />
                        {comments.map((comment) => {
                            return (
                                <CommentCard
                                    key={comment.id}
                                    comment={comment}
                                    postingId={review.id}
                                    postingAuthorId={review.author.id}
                                    currentUser={currentUser}
                                    token={token}
                                    onDeleteComment={onDeleteComment}
                                    onRepliesUpdate={onRepliesChangeHandler}
                                />
                            )
                        })
                        }
                        {showMoreComments && <div className={'fs-6'}>
                            <i className={'fal fa-comment-alt text-primary me-2'}></i>
                            <a
                                onClick={() => onListComments()}
                                className={'text-secondary'}>
                                See all {commentCount} comments
                            </a>
                        </div>}
                    </div>
                )
            } else {
                return (
                    <>
                        <SectionHeaderTag text={'Be the first to comment'} classes={'pb-2'} />
                        {renderCommentForm()}
                    </>
                )
            }
        }
    }

    const renderBackLink = () => {
        if(isMobile) {
            return (
                <a /*onClick={() => props.onPrev()}*/ onClick={() => props.onCancel()}>
                    <i className={'fal fa-angle-left text-secondary fs-6'}></i>
                </a>
            )
        } else {
            return (
                <>
                    <i className={'fal fa-angle-left pe-1 text-secondary'}></i>
                    <a /*onClick={() => props.onPrev()}*/ onClick={() => props.onCancel()}
                       className={'text-secondary'}>Back {props.isShareLink ? 'to all reviews' : ''}</a>
                </>
            )
        }
    }

    const handleNextReview = () => {
        props.onNext();
        modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const renderNextLink = () => {
        if(props.hasMoreReviews) {
            if(isMobile) {
                return (
                    <a onClick={() => handleNextReview()}>
                        <i className={'fal fa-angle-right text-secondary fs-6'}></i>
                    </a>
                )
            } else {
                return (
                    <>
                        <a onClick={() => handleNextReview()} className={'text-secondary'}>Next review</a>
                        <i className={'fal fa-angle-right ps-1 text-secondary'}></i>
                    </>
                )
            }
        }
    }

    const renderReportLeadGen = () => {
        if(currentUser) {
            return (
                <a className={'text-blue-link hover-blue-hover fw-6'} onClick={() => onToggleLeadGen({
                    directReportUrl: product.report?.link,
                    directReportDownload: true,
                    reportTopic: product.report?.title,
                    withConfirmationScreen: false,
                    leadCode: 'DOWNLOAD_REPORT',
                    modalOverlay: true,
                    sendReport: true,
                    hiddenTags: {
                        ...leadAttributes?.lead_attributes, itemid: product.id, 'lead[product_id]': product.id, 'lead[lead_type_data]': product.id, 'lead[lead_code]': 'DOWNLOAD_REPORT' }
                })}
                   data-gtm-event-region={'product-review'} data-gtm-event-element={'download-report'}>{product.name} Buyer's Guide</a>
            )
        } else {
            return (
                <a className={'text-blue-link hover-blue-hover fw-6'} href={report.url}
                   data-gtm-event-region={'product-review'} data-gtm-event-element={'download-report'}>{product.name} Buyer's Guide</a>
            )
        }
    }

    const renderReport = () => {
        if(report.url) {
            // let styleVars = {
            //     '--divider-overlay-txt': "'or'"
            // }
            return (
                <Card className={'shadow-none p-3'}>
                    <div className={'text-secondary fs-5 fw-bolder'}>Product Buyer's Guide</div>
                    <div className={'subheader-sm py-2'}>Get pricing advice, tips, use cases and valuable features from real users of this product.</div>
                    <div className={'d-flex align-items-center'}>
                        <i className={'fal fa-file-pdf text-reddark fs-3 pe-1'} />
                        {renderReportLeadGen()}
                    </div>
                    {/*<div className={'divider-horizontal with-overlay mt-2 mb-3'} style={styleVars} />*/}
                </Card>
            )
        }
    }

    const renderRelatedProducts = () => {
        if(relatedProducts) {
            return (
                <>
                    <SectionHeaderTag text={'Related products'}  classes={'pb-2'} />
                    <ListGroup variant={'flush'}>
                        {relatedProducts.map(p => {
                            return (
                                <ListGroup.Item className={'bg-light px-0'} key={`rpid_${p.id}`}>
                                    <ProductCard
                                        product={p}
                                        displayInCard={false}
                                        showRating={true}
                                        logoSize={40}
                                    />
                                </ListGroup.Item>
                            )
                        })}
                    </ListGroup>
                </>
            )
        }
    }

    const renderReviewAuthor = () => {
        return (
            <AuthorSideBar
                key={review.id}
                review={review}
                product={product}
                extraData={ { lead_gen_attributes: leadAttributes, smarter_questions: leadAttributes.inquiry_call_questions } }
                modalOverlay={true}
                toggleRegistration={onToggleLeadGen}
                tooltips={tooltips}
            />
        )
    }

    const renderRatingAndSentiment = () => {
        // if (review.rating || review.sentiment) {
        if (review.rating) {
            return (
              <Card className={'shadow-none px-3 py-1'}>
                  {review.rating &&
                      <div className={'d-flex justify-content-between content-styling fs-6 text-secondary my-2'}>
                          <div className={'text-nowrap'}>Rating <TooltipTrigger id={`rating-tt_${review.id}`} placement={'bottom'} content={tooltips.reviewer_rating}>
                              <i className={'fal fa-question-circle font-12 text-blue-link px-1'}/></TooltipTrigger>
                          </div>
                          <div className={'filler-dots'}/>
                          <div className="fw-bold">{review.ten_point_rating}</div>
                      </div>}
                  {/*{valueExists(review.sentiment) &&*/}
                  {/*    <div className={'d-flex justify-content-between content-styling fs-6 text-secondary my-2'}>*/}
                  {/*        <div className={'text-nowrap'}>Interview sentiment <TooltipTrigger id={`ss-tt_${review.id}`} placement={'bottom'} content={SENTIMENT_SCORE_TOOLTIP}>*/}
                    {/*            <i className={'fal fa-question-circle fs-6 text-blue-link ps-1'} /></TooltipTrigger></div>*/}
                    {/*        <div className={'filler-dots'} />*/}
                    {/*        <div className="fw-bold">{review.sentiment}</div>*/}
                    {/*    </div>}*/}
                </Card>
            )
        }
    }

    return (
        <Modal
            id={'SingleReview'}
            show={show}
            onHide={() => props.onCancel(false)}
            size={'lg'}
            backdrop={false}
            fullscreen={true}
            enforceFocus={false}
            style={{top: isMobile ? '0' : '50px', overflowY: 'auto'}}
        >
            <Modal.Header className={'py-2'}>
                <Container fluid={'lg'}>
                    <Row className={'align-items-center justify-content-between'}>
                        <Col className={'px-0'}>{renderBackLink()}</Col>
                        <Col className={'d-none d-md-flex justify-content-center'}>
                            <ProductCard
                                product={product}
                                displayInCard={false}
                                showRating={true}
                                colClasses={'col-10'}
                                showReviewCount={true}
                            />
                        </Col>
                        <Col className={'d-md-none text-secondary fw-bold text-center'} style={{ flexGrow: 6 }}>{product.name}</Col>
                        <Col className={'text-end px-0'}>{renderNextLink()}</Col>
                    </Row>
                </Container>
            </Modal.Header>
            <Modal.Body className={`px-0 ${props.restrictedAccess && 'overflow-hidden'}`} ref={modalBodyRef} id={'review-modal'}>
                <Container fluid="lg">
                    <Row className={'p-3 px-md-2'}>
                        <Col sm={12} md={8} className={'px-0'}>
                            <ReviewCard
                                review={review}
                                sections={reviewSections}
                                showProduct={false}
                                showLikes={true}
                                showBody={true}
                                collapsedBody={false}
                                showProsCons={true}
                                showNewCommentForm={false}
                                showReviewActions={false}
                                showProductVersion={true}
                                titleFont={24}
                                currentUser={currentUser}
                                token={token}
                                displayInCard={false}
                                showUser={false}
                                showRating={false}
                                showAudio={true}
                                playAudio={onToggleAudio}
                                selectedAudio={selectedAudio}
                                toggleRegistration={onToggleLeadGen}
                                modalOverlay={true}
                                tooltips={tooltips}
                                showShortSummary={true}
                                leadData={genericLeadData}
                                includeForeignLanguageLink={true}
                            />
                            {renderReviewActions()}
                            {comments.length ? renderCommentForm() : ''}
                        </Col>
                        <Col sm={12} md={4} className={'pt-2 px-0 pt-md-0 ps-md-4'}>
                            <div className={'sticky-md-top'} style={{ top: '15px' }}>
                                {renderReviewAuthor()}
                                {renderRatingAndSentiment()}
                                {renderReport()}
                                <div className={'text-center'}>
                                    <a href={`${product.url}#reviews-container`} onClick={() => props.onCancel()} className={'text-blue-link hover-blue-hover fw-bolder'}>
                                        {reviewCount == 2 ? 'Read 1 more review' : `Explore all ${reviewCount} reviews`}
                                    </a>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
                <Container fluid className={'bg-light border-top py-3'}>
                    <Container fluid="lg" className={'mb-5'}>
                        <Row>
                            <Col sm={12} md={8}>
                                {renderComments()}
                            </Col>
                            <Col md={4} className={'d-none d-md-block ps-4'}>
                                {renderRelatedProducts()}
                            </Col>
                        </Row>
                    </Container>
                </Container>
            </Modal.Body>
        </Modal>
    )
}
ReviewModal.displayName = 'ReviewModal';
export default ReviewModal;